// src/Pages/Progetti/appunti/ProgettoAppuntiList.tsx

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { get, postForm } from '../../../hook/request';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import Wysiwyg from '../../../Components/Forms/EditorText';
import DeleteConfirm from '../../../Components/Modals/Delete'

type AppuntoApi = {
    ID: number;
    progettoID: number;
    descrizione: string;
    path: string | null;
    data_update: string;
    userID: number;
    titolo: string;
    data_ins: string;
    cancellato: number;
    data_del: string | null;
    cognome: string;
    nome: string;
    cognome_upd?: string | null;
    nome_upd?: string | null;
    email: string;
    edit: number;
    condiviso?: number | string | boolean;
};

type Appunto = {
    id: string;
    titolo: string;
    descrizione: string;
    path: string | null;
    autore: string;
    creatoDa: string;
    modificatoDa: string;
    email: string;
    dataIns: string;
    dataUpdate: string;
    edit: number;
    userID: number;
    condiviso: number;
};

type Props = {
    refreshKey: number;
};

function normalizeAttachmentUrl(path: string | null | undefined): string | null {
    if (!path) return null;

    const cleaned = path.trim().replace(/^"+|"+$/g, '');
    if (!cleaned) return null;

    if (/^https?:\/\//i.test(cleaned)) {
        return cleaned;
    }

    return `https://${cleaned.replace(/^\/+/, '')}`;
}

function getCurrentUserId(): number | null {
    const raw = localStorage.getItem('codiceUtente');
    if (!raw) return null;

    const parsed = Number(raw.replace(/"/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
}

function normalizeCondiviso(value: AppuntoApi['condiviso']): number {
    return value === 1 || value === '1' || value === true ? 1 : 0;
}

function formatName(nome?: string | null, cognome?: string | null): string {
    return `${nome ?? ''} ${cognome ?? ''}`.trim();
}

function formatDateTime(value?: string | null): string {
    if (!value) return '';

    const date = new Date(value.replace(' ', 'T'));
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export default function ProgettoAppuntiList({ refreshKey }: Props) {
    const { id: progettoId } = useParams<{ id: string }>();
    const currentUserId = getCurrentUserId();
    const [items, setItems] = useState<Appunto[]>([]);
    const [loading, setLoading] = useState(true);
    const [version, setVersion] = useState(0);

    // stato modale di edit
    const [editOpen, setEditOpen] = useState(false);
    const [editSaving, setEditSaving] = useState(false);
    const [editing, setEditing] = useState<Appunto | null>(null);
    const [editTitle, setEditTitle] = useState('');
    const [editHtml, setEditHtml] = useState('');
    const [editCondiviso, setEditCondiviso] = useState(false);
    const [editFile, setEditFile] = useState<File | null>(null);
    const [editFileInputKey, setEditFileInputKey] = useState(0);

    useEffect(() => {
        if (!progettoId) {
            setLoading(false);
            return;
        }

        const loadAppunti = async () => {
            try {
                setLoading(true);
                const res = await get<AppuntoApi[]>(
                    `/progetti/appunti?ID=${encodeURIComponent(progettoId)}`
                );

                const rawList: AppuntoApi[] = Array.isArray((res as any)?.data)
                    ? (res as any).data
                    : Array.isArray(res)
                        ? (res as AppuntoApi[])
                        : [];

                const mapped: Appunto[] = rawList
                    .filter(a => a.cancellato === 0)
                    .map(a => {
                        const condiviso = normalizeCondiviso(a.condiviso);
                        const creatoDa = formatName(a.nome, a.cognome);
                        const modificatoDa = formatName(a.nome_upd, a.cognome_upd);

                        return {
                            id: String(a.ID),
                            titolo: a.titolo || '',
                            descrizione: a.descrizione || '',
                            path: normalizeAttachmentUrl(a.path),
                            autore: creatoDa,
                            creatoDa,
                            modificatoDa,
                            email: a.email || '',
                            dataIns: a.data_ins || a.data_update,
                            dataUpdate: a.data_update || '',
                            edit: a.edit || 0,
                            userID: Number(a.userID),
                            condiviso,
                        };
                    });

                setItems(mapped);
            } catch (err) {
                console.error('Errore caricando la lista appunti:', err);
                toast.error('Impossibile caricare la lista degli appunti');
            } finally {
                setLoading(false);
            }
        };

        loadAppunti();
    }, [progettoId, refreshKey, version]);

    if (!progettoId) {
        return <div>Nessun progetto selezionato.</div>;
    }


    const handleOpenEdit = (appunto: Appunto) => {
        setEditing(appunto);
        setEditTitle(appunto.titolo);
        setEditHtml(appunto.descrizione);
        setEditCondiviso(appunto.condiviso === 1);
        setEditFile(null);
        setEditFileInputKey(prev => prev + 1);
        setEditOpen(true);
    };

    const handleCloseEdit = () => {
        if (editSaving) return;
        setEditOpen(false);
        setEditing(null);
        setEditFile(null);
    };


    const handleSaveEdit = async () => {
        if (!editing || !progettoId) return;
        try {
            setEditSaving(true);
            const formData = new FormData();
            formData.append('ID', String(Number(editing.id)));
            formData.append('titolo', editTitle);
            formData.append('appunti', editHtml);
            formData.append('condiviso', editCondiviso ? '1' : '0');

            if (editFile) {
                formData.append('file', editFile);
            }

            await postForm(`/progetti/appunti?ID=${encodeURIComponent(progettoId)}`, formData);
            toast.success('Appunto aggiornato con successo');
            setEditOpen(false);
            setEditing(null);
            setEditFile(null);
            setEditFileInputKey(prev => prev + 1);
            setVersion(v => v + 1);
        } catch (err: any) {
            const msg = err?.response?.data?.message ?? err?.message ?? 'Errore di rete';
            toast.error(`Errore aggiornamento appunto: ${msg}`);
        } finally {
            setEditSaving(false);
        }
    };

    return (
        <>
            <div className="card mt-3">
                <div className="card-header ps-0">
                    <h5 className="mb-0 ps-0">Tutti gli appunti</h5>
                </div>

                <div className="card-body px-0">
                    {loading ? (
                        <p>Caricamento appunti...</p>
                    ) : items.length === 0 ? (
                        <p className="text-muted">Non ci sono appunti salvati.</p>
                    ) : (
                        <div className="d-flex flex-column gap-3">
                            {items.map(appunto => (
                                <div key={appunto.id} className="border rounded p-3 w-100">
                                    <h4 className="mb-1 bold">{appunto.titolo || '(Senza titolo)'}</h4>

                                    {appunto.descrizione && (
                                        <div
                                            className="mt-2 mb-4"
                                            style={{ whiteSpace: 'normal' }}
                                            dangerouslySetInnerHTML={{ __html: appunto.descrizione }}
                                        />
                                    )}

                                    <div className="text-muted small m1-2 grey d-flex flex-row justify-content-between">
                                        <div>
                                            {appunto.condiviso === 1 ? (
                                                <div className="d-flex flex-column align-items-start gap-1">
                                                    <div>
                                                        Creato da: <strong>{appunto.creatoDa || '-'}</strong>
                                                        {appunto.dataIns && (
                                                            <span className="ms-2">
                                                                {formatDateTime(appunto.dataIns)}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <div>
                                                        Modificato da:{' '}
                                                        <strong>{appunto.modificatoDa || '-'}</strong>
                                                        {appunto.dataUpdate && (
                                                            <span className="ms-2">
                                                                {formatDateTime(appunto.dataUpdate)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                <span>
                                                    <strong>{appunto.autore}</strong>
                                                </span>
                                            )}

                                            {appunto.condiviso === 1 && (
                                                <span className="badge bg-info ms-2">
                                                    Condiviso
                                                </span>
                                            )}

                                            {appunto.condiviso !== 1 && appunto.dataIns && (
                                                <span className="ms-2">
                                                    ·{' '}
                                                    {formatDateTime(appunto.dataIns)}
                                                </span>
                                            )}
                                        </div>

                                        <div>
                                            <div
                                                style={{ fontSize: '1rem' }}
                                                className="d-flex flex-row gap-2"
                                            >
                                                {appunto.path && (
                                                    <a
                                                        href={appunto.path}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        title="Scarica allegato"
                                                    >
                                                        <i
                                                            className="fa-solid fa-file"
                                                            style={{ cursor: 'pointer' }}
                                                        ></i>
                                                    </a>
                                                )}

                                                {(appunto.edit === 1 || appunto.condiviso === 1) && (
                                                    <>
                                                        <i
                                                            className="fa-solid fa-pen-to-square"
                                                            style={{ cursor: 'pointer' }}
                                                            title="Modifica appunto"
                                                            onClick={() => handleOpenEdit(appunto)}
                                                        ></i>

                                                        {appunto.edit === 1 && (
                                                            <DeleteConfirm
                                                                url={`/progetti/appunti?ID=${appunto.id}`}
                                                                onSuccess={() => {
                                                                    setVersion(v => v + 1);
                                                                }}
                                                                trigger={
                                                                    <i
                                                                        className="fa-solid fa-trash"
                                                                        style={{ cursor: 'pointer' }}
                                                                        title="Cancella appunto"
                                                                    ></i>
                                                                }
                                                            />
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* MODALE EDIT */}
            <Modal isOpen={editOpen} toggle={handleCloseEdit} size="lg">
                <ModalHeader toggle={handleCloseEdit}>
                    Modifica appunto
                </ModalHeader>
                <ModalBody>
                    <div className="mb-3">
                        <label className="form-label">Titolo</label>
                        <input
                            type="text"
                            className="form-control"
                            value={editTitle}
                            disabled={editSaving}
                            onChange={e => setEditTitle(e.target.value)}
                        />
                    </div>

                    <div className="mb-0">
                        <label className="form-label">Descrizione</label>
                        <Wysiwyg
                            value={editHtml}
                            onChange={setEditHtml}
                            height={260}
                            disabled={editSaving}
                        />
                    </div>

                    <div className="mt-3 mb-0">
                        <label className="form-label">Allegato</label>
                        <input
                            key={editFileInputKey}
                            type="file"
                            className="form-control"
                            disabled={editSaving}
                            onChange={e => setEditFile(e.target.files?.[0] || null)}
                        />
                    </div>

                    {editing && currentUserId !== null && editing.userID === currentUserId && (
                        <div className="form-check mt-3">
                            <input
                                id="edit-appunto-condiviso"
                                type="checkbox"
                                className="form-check-input"
                                checked={editCondiviso}
                                disabled={editSaving}
                                onChange={e => setEditCondiviso(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="edit-appunto-condiviso">
                                Condividi appunto con gli altri
                            </label>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={handleCloseEdit} disabled={editSaving}>
                        Annulla
                    </Button>
                    <Button color="primary" onClick={handleSaveEdit} disabled={editSaving}>
                        {editSaving ? 'Salvo...' : 'Salva modifiche'}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
}
